import React, { useEffect } from 'react';
import { BreadCrumb, FlattenedRoute, PageHeaders } from 'config/shared/types';
import { useFeatureFlags } from '@cko/dashboard-shared';
import { useParams } from 'react-router-dom';
import { LayoutGridItem } from '@cko/primitives';
import { SecondaryHeader } from '@cko/patterns';
import { useDynamicHeaderContext, useNavigation } from '../../../providers';
import { FeatureFlagId } from '../../../config/shared/enums/featureFlagIds';
import { BreadcrumbsItem, MainHeader } from './components';
import { StyledLayoutGrid } from './PageHeader.styles';

interface Props {
  headerConfig: PageHeaders;
  route: FlattenedRoute;
  routeId: string;
}

export const PageHeader = ({ headerConfig, route, routeId }: Props) => {
  const { getUrlForRoute, filteredMenuItems } = useNavigation();
  const { pageDescription } = useDynamicHeaderContext();
  const { getFeatureFlag } = useFeatureFlags();
  const isNewHeaderEnabled = getFeatureFlag(FeatureFlagId.NEW_HEADINGS);
  const params = useParams();

  const breadCrumbItems: Array<BreadcrumbsItem> =
    route.breadcrumbs?.map((value: BreadCrumb) => {
      return {
        label: value.title,
        href: value.routeId ? getUrlForRoute(value.routeId, params) || '' : '',
        selected: value.routeId === routeId,
      };
    }) || [];

  const menuItems = headerConfig.menu_id ? filteredMenuItems[headerConfig.menu_id] : [];
  const hasMenu = menuItems && menuItems.length > 0;

  // sets the page title to be the same as the H1 element.
  useEffect(() => {
    if (isNewHeaderEnabled) {
      document.title = `${headerConfig.main_heading?.title} - Dashboard`;
    }
  }, [headerConfig.main_heading?.title, isNewHeaderEnabled]);

  //need to return null if not enabled and not showing a menu to prevent a gap being rendered on the page, can delete this out
  //once fully enabled.
  if (!isNewHeaderEnabled && !hasMenu) {
    return null;
  }

  return (
    <StyledLayoutGrid fullWidth>
      {headerConfig.main_heading && (
        <LayoutGridItem span={24}>
          <MainHeader
            config={headerConfig.main_heading}
            dynamicDescription={pageDescription}
            breadCrumbItems={breadCrumbItems}
            menuItems={hasMenu ? menuItems : undefined}
          />
        </LayoutGridItem>
      )}

      {headerConfig.secondary_heading && isNewHeaderEnabled && (
        <LayoutGridItem span={24}>
          <SecondaryHeader
            description={headerConfig.secondary_heading.description}
            title={headerConfig.secondary_heading.title}
          />
        </LayoutGridItem>
      )}
    </StyledLayoutGrid>
  );
};
